import type { ReactNode } from 'react';
import { StyleSheet, Text, type StyleProp, type TextProps, type TextStyle } from 'react-native';
import { tokens } from '../../theme/tokens';

type AppTextVariant = 'hero' | 'title' | 'subtitle' | 'body' | 'caption' | 'metric';
type AppTextTone =
  | 'default'
  | 'muted'
  | 'subtle'
  | 'primary'
  | 'accent'
  | 'danger'
  | 'success'
  | 'warning';

type AppTextProps = TextProps & {
  children: ReactNode;
  variant?: AppTextVariant;
  tone?: AppTextTone;
  weight?: keyof typeof tokens.fontWeight;
  align?: TextStyle['textAlign'];
  style?: StyleProp<TextStyle>;
};

const variantStyles: Record<AppTextVariant, TextStyle> = {
  hero: {
    fontSize: tokens.fontSize.xxl,
    lineHeight: tokens.lineHeight.xxl,
  },
  title: {
    fontSize: tokens.fontSize.xl,
    lineHeight: tokens.lineHeight.xl,
  },
  subtitle: {
    fontSize: tokens.fontSize.lg,
    lineHeight: tokens.lineHeight.lg,
  },
  body: {
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
  },
  caption: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  metric: {
    fontSize: tokens.fontSize.xxl,
    lineHeight: tokens.lineHeight.xxl,
  },
};

const toneStyles: Record<AppTextTone, TextStyle> = {
  default: { color: tokens.colors.text },
  muted: { color: tokens.colors.textMuted },
  subtle: { color: tokens.colors.textSubtle },
  primary: { color: tokens.colors.primary },
  accent: { color: tokens.colors.accent },
  danger: { color: tokens.colors.danger },
  success: { color: tokens.colors.success },
  warning: { color: tokens.colors.warning },
};

export function AppText({
  children,
  variant = 'body',
  tone = 'default',
  weight = 'regular',
  align,
  style,
  ...textProps
}: AppTextProps) {
  return (
    <Text
      {...textProps}
      style={[
        styles.base,
        variantStyles[variant],
        toneStyles[tone],
        { fontWeight: tokens.fontWeight[weight], textAlign: align },
        style,
      ]}
    >
      {children}
    </Text>
  );
}

const styles = StyleSheet.create({
  base: {
    letterSpacing: 0,
  },
});
